/**
 * LIVE-SITES — shared "freshest-wins" merge used by BOTH the one-shot
 * `fetchSites` load AND the real-time `onSnapshot` subscription that keeps
 * company-scoped site state (incl. `pinnedReportIds`) propagating live to
 * every user with access.
 *
 * Background: `fetchSites` previously inlined this merge (store/index.ts).
 * Because a one-shot `getDocs` only ran at login, a pin/unpin made by one
 * user reached other users ONLY on reload. The live `onSnapshot` listener
 * funnels its snapshot through the SAME merge so the two paths can never
 * diverge — identical RBAC scope (the listener mirrors `fetchSites`'s two
 * queries) AND identical conflict resolution.
 *
 * Freshest-wins (unchanged from the original inline logic): a site whose
 * LOCAL `siteConfigUpdatedAt` is STRICTLY newer than the incoming doc keeps
 * its locally-edited config fields (`projectIntent` / `typedPreferencesDefaults`
 * / `siteContext` + provenance). This protects a just-made in-memory edit
 * (optimistic `updateSite`) from being clobbered by a slightly-older snapshot
 * tick — the common race when a user edits intent and a peer's unrelated
 * write lands a moment later. Every OTHER field (incl. `pinnedReportIds`,
 * `latestReportId`, locks, …) always takes the incoming canonical value.
 *
 * `preserveLocalLayers` (live path only): the snapshot listener does NOT
 * re-read each site's `siteLayers` subcollection on every tick (that would
 * be an N-doc fan-out per snapshot). When set, the merge carries the
 * already-loaded `siteLayers` (and the transient `_sourceScope` / `_ownerUid`
 * annotations for OWN sites, which the listener's own-sites query does not
 * recompute) from the existing in-store site forward. `fetchSites` leaves it
 * off because it freshly loads layers + stamps annotations itself.
 */

import type { SiteEntity, SiteLayer } from '../../types/site';
import { structuralEqual } from '../structuralEqual';
import { SITE_BOUNDARY_READ_FIELDS, sameSiteBoundaryReadBasis } from '../siteBoundary/siteBoundaryReadBasis';
import { isVerifiedCompleteSiteLayerMirror } from '../siteLayers/canonicalLayerResolution';

export interface MergeSitesArgs {
  /** Current in-store sites (the merge target). */
  current: SiteEntity[];
  /**
   * Incoming canonical sites — already deduped + scope-tagged + (for
   * `fetchSites`) layer-loaded by the caller. Own sites FIRST so they win any
   * positional tie-break, exactly as the original inline merge required.
   */
  incoming: SiteEntity[];
  /**
   * Live-snapshot only: carry `siteLayers` + transient annotations from the
   * matching in-store site forward (the listener does not reload them).
   */
  preserveLocalLayers?: boolean;
  /** Snapshot at the start of an asynchronous read, before worker work. */
  readStartedWith?: readonly SiteEntity[];
}

function parseFiniteMs(iso: string | undefined): number {
  if (!iso) return 0;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Merge `incoming` canonical sites onto `current` in-store sites, applying
 * the freshest-wins guard for locally-edited config fields. Returns the
 * merged array (same order as `incoming`).
 *
 * IDENTITY (2026-10-07): a site whose merged result is structurally equal to
 * the in-store object comes back AS that object. The live listener fires on
 * every write to any site doc, and a fresh-but-equal object was a changed
 * `site` prop / selector value for the whole site page. Strict structural
 * equality means this never changes what is merged — only whether a new
 * object is allocated.
 */
export function mergeSitesIntoStore({
  current,
  incoming,
  preserveLocalLayers = false,
  readStartedWith,
}: MergeSitesArgs): SiteEntity[] {
  const byId = new Map<string, SiteEntity>(current.map((s) => [s.id, s]));

  const startedById = readStartedWith && new Map(readStartedWith.map(site => [site.id, site]));
  return incoming.map((persisted) => {
    const local = byId.get(persisted.id);
    let merged = mergeOne(persisted, local, preserveLocalLayers);
    const started = startedById?.get(persisted.id);
    if (local && started && (
      !sameSiteBoundaryReadBasis(started, local)
      || local.siteLayersRevision !== started.siteLayersRevision
      || !structuralEqual(local.siteLayers, started.siteLayers)
    )) {
      // An edit or newer live snapshot landed while the worker was running.
      // Carry its geometry and aliases together, never mix revision halves.
      merged = { ...merged };
      for (const field of SITE_BOUNDARY_READ_FIELDS) {
        if (local[field] === undefined) delete merged[field];
        else Object.assign(merged, { [field]: local[field] });
      }
      merged.siteLayers = local.siteLayers;
      merged.siteLayersRevision = local.siteLayersRevision;
      merged.siteLayersCanonicalVersion = local.siteLayersCanonicalVersion;
      merged.siteLayerCount = local.siteLayerCount;
      merged.siteLayersMirrorComplete = local.siteLayersMirrorComplete;
      merged._siteLayersCanonicalComplete = local._siteLayersCanonicalComplete;
      merged._siteLayersHydrationPending = local._siteLayersHydrationPending;
      merged._siteLayersHydrationError = local._siteLayersHydrationError;
    }
    return local && structuralEqual(local, merged) ? local : merged;
  });
}

export interface HydratedSiteLayersPatch {
  siteId: string;
  /** The `siteLayersRevision` observed on the parent doc when the layer read started. */
  expectedRevision: number | undefined;
  /** The initial mirror layers published in Phase 1 for this site. */
  phase1Layers: readonly SiteLayer[] | undefined;
  /** Resolved canonical layers (when `canonicalComplete` is true) or fallback layers. */
  layers: SiteLayer[];
  /** True only when canonical subcollection resolution succeeded (or mirror was verified complete). */
  canonicalComplete: boolean;
  /** Error message when canonical subcollection read failed. */
  hydrationError?: string;
}

/**
 * Patch delayed Phase 2 canonical `/layers` results onto the CURRENT in-store
 * site records without replaying stale Phase 1 parent snapshots.
 *
 * - Preserves every current parent field (`pinnedReportIds`, `reportIds`,
 *   `simulationIds`, `documentIds`, locks, config, boundary, etc.) untouched.
 * - Applies `layers` ONLY if the current site in the store still has the same
 *   `siteLayersRevision` and its `siteLayers` has not been mutated since Phase 1.
 * - If a newer layer write (`siteLayersRevision` changed or `siteLayers` moved
 *   away from `phase1Layers`) already landed in the store, keeps the current
 *   store layers and revision intact.
 * - If `canonicalComplete` is false (hydration failed), keeps read-only display
 *   layers while marking `_siteLayersCanonicalComplete: false`,
 *   `_siteLayersHydrationPending: false`, and `_siteLayersHydrationError`.
 */
export function applyHydratedSiteLayersToCurrentSites(
  currentSites: readonly SiteEntity[],
  patchesById: ReadonlyMap<string, HydratedSiteLayersPatch>,
): SiteEntity[] {
  if (patchesById.size === 0) return currentSites as SiteEntity[];
  let changed = false;
  const next = currentSites.map((current) => {
    const patch = patchesById.get(current.id);
    if (!patch) return current;

    const revisionMoved = (current.siteLayersRevision ?? 0) !== (patch.expectedRevision ?? 0);
    const layersMovedSincePhase1 =
      patch.phase1Layers !== undefined
      && current.siteLayers !== patch.phase1Layers
      && !structuralEqual(current.siteLayers, patch.phase1Layers);

    if (revisionMoved || layersMovedSincePhase1) {
      if (current._siteLayersHydrationPending) {
        changed = true;
        return {
          ...current,
          _siteLayersHydrationPending: false,
        };
      }
      return current;
    }

    if (!patch.canonicalComplete) {
      const nextFallbackLayers = patch.layers.length > 0 ? patch.layers : (current.siteLayers ?? []);
      const nextSite: SiteEntity = {
        ...current,
        siteLayers: nextFallbackLayers,
        _siteLayersCanonicalComplete: false,
        _siteLayersHydrationPending: false,
        _siteLayersHydrationError: patch.hydrationError ?? 'Failed to load canonical site map layers.',
      };
      if (structuralEqual(current, nextSite)) return current;
      changed = true;
      return nextSite;
    }

    const nextSite: SiteEntity = {
      ...current,
      siteLayers: patch.layers,
      _siteLayersCanonicalComplete: true,
      _siteLayersHydrationPending: false,
      _siteLayersHydrationError: undefined,
    };
    if (structuralEqual(current, nextSite)) return current;
    changed = true;
    return nextSite;
  });
  return changed ? next : (currentSites as SiteEntity[]);
}

function mergeOne(
  persisted: SiteEntity,
  local: SiteEntity | undefined,
  preserveLocalLayers: boolean,
): SiteEntity {
  // When the listener delivers a doc, it has no `siteLayers` (subcollection)
  // and — for OWN sites — no transient `_sourceScope` / `_ownerUid`. Carry
  // the already-loaded values forward so a live tick never blanks the map
  // layers or breaks the owner-path write resolution.
  let base = persisted;
  if (preserveLocalLayers && local) {
    const sameLayerRevision = (local.siteLayersRevision ?? 0) === (persisted.siteLayersRevision ?? 0);
    const persistedMirrorComplete = isVerifiedCompleteSiteLayerMirror(persisted);
    base = {
      ...persisted,
      // Live snapshots carry only the bounded parent-document MIRROR. Dense
      // CAD rows intentionally live in `/layers`, so even a non-empty
      // incoming array can be incomplete (Redhead: 42 mirror rows vs 188
      // canonical rows). Once fetchSites has hydrated a local array at the
      // same revision, retain it wholesale; if a remote write advanced
      // `siteLayersRevision`, only adopt the mirror as complete when verified.
      siteLayers: sameLayerRevision && Array.isArray(local.siteLayers)
        ? local.siteLayers
        : (persistedMirrorComplete && Array.isArray(persisted.siteLayers)
          ? persisted.siteLayers
          : (Array.isArray(local.siteLayers) ? local.siteLayers : persisted.siteLayers)),
      ...(sameLayerRevision
        ? {
            ...(local._siteLayersCanonicalComplete !== undefined
              ? { _siteLayersCanonicalComplete: local._siteLayersCanonicalComplete }
              : {}),
            ...(local._siteLayersHydrationPending !== undefined
              ? { _siteLayersHydrationPending: local._siteLayersHydrationPending }
              : {}),
            ...(local._siteLayersHydrationError !== undefined
              ? { _siteLayersHydrationError: local._siteLayersHydrationError }
              : {}),
          }
        : {
            _siteLayersCanonicalComplete: persistedMirrorComplete,
            _siteLayersHydrationPending: false,
            _siteLayersHydrationError: undefined,
          }),
      // Re-stamp transient annotations when the incoming doc omitted them
      // (own-sites snapshot path doesn't recompute these).
      _sourceScope: persisted._sourceScope ?? local._sourceScope,
      ...(persisted._ownerUid ?? local._ownerUid
        ? { _ownerUid: persisted._ownerUid ?? local._ownerUid }
        : {}),
    };
  } else if (
    !preserveLocalLayers
    && local
    && persisted._siteLayersHydrationPending === true
    && local._siteLayersCanonicalComplete === true
    && !local._siteLayersHydrationPending
    && !local._siteLayersHydrationError
    && (local.siteLayersRevision ?? 0) === (persisted.siteLayersRevision ?? 0)
    && Array.isArray(local.siteLayers)
  ) {
    base = {
      ...persisted,
      siteLayers: local.siteLayers,
      _siteLayersCanonicalComplete: true,
      _siteLayersHydrationPending: false,
      _siteLayersHydrationError: undefined,
    };
  }

  if (!local?.siteConfigUpdatedAt) return base;

  const localAt = parseFiniteMs(local.siteConfigUpdatedAt);
  const remoteAt = parseFiniteMs(base.siteConfigUpdatedAt);
  // Strict: equal timestamps fall through to the canonical incoming doc so a
  // same-millisecond apply-to-site write doesn't pin stale local config.
  if (localAt <= remoteAt) return base;

  return {
    ...base,
    projectIntent: local.projectIntent ?? base.projectIntent,
    typedPreferencesDefaults:
      local.typedPreferencesDefaults ?? base.typedPreferencesDefaults,
    siteContext: local.siteContext ?? base.siteContext,
    siteConfigUpdatedAt: local.siteConfigUpdatedAt,
    siteConfigUpdatedBy: local.siteConfigUpdatedBy,
  };
}
